import React, { useCallback, useMemo, useState } from 'react'
import Button from '@mui/material/Button'
import { ENTITY_TYPES, LocationDTO } from '@sales-center/common'
import { EntityDetail } from '@user/store/map/typings'
import { ErrorModalState } from './hooks'
import Menu from '@mui/material/Menu'
import { ScheduleMenuItem } from '@user/components/Scheduling/SchedulingWizard/ScheduleButton/ScheduleMenuItem'
import { CWQDialogContextProvider } from '@user/components/SoldServices/CWQDialogContext'
import { SchedulingWizardContextProvider } from '@user/components/Scheduling/SchedulingWizard/wizardContext/SchedulingWizardContextProvider'
import { useSchedulingEnabled } from '@user/store/scheduling'
import { useSchedulingVisibility } from '@user/store/closingRequirements'
import { checkIsClosed } from '../utils'
import CircularProgress from '@mui/material/CircularProgress'

type Props = {
  entityType: ENTITY_TYPES
  entityDetails: EntityDetail
  canUpdate: boolean
  isClosed: boolean
  serviceLocation: LocationDTO
  setErrorModal: (errorModal: ErrorModalState) => void
  refetchEntityDetails: () => Promise<EntityDetail | null>
  onLeadAutoClosed?: () => void
}

const SCHEDULE_LOCATION_ERROR =
  'You must match this record to a PestPac location before you can schedule.'

export function Scheduling({
  entityType,
  entityDetails,
  canUpdate,
  setErrorModal,
  refetchEntityDetails,
  onLeadAutoClosed,
}: Props) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [isLoading, setIsLoading] = useState(false)
  const scheduleButtonRef = React.useRef<HTMLButtonElement | null>(null)
  const open = Boolean(anchorEl)

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  async function runScheduleFlow() {
    setIsLoading(true)
    const freshEntityDetails = await refetchEntityDetails()
    setIsLoading(false)
    if (!canUpdate) {
      setErrorModal({
        open: true,
        title: 'Schedule',
        message: "You don't have permission to perform this action.",
      })
      return
    }

    const currentIsClosed = checkIsClosed(freshEntityDetails?.salesFunnelStage)
    if (currentIsClosed) {
      setErrorModal({
        open: true,
        title: 'Schedule',
        message: 'You cannot schedule for a closed record.',
      })
      return
    }

    if (!freshEntityDetails?.externalLocationId) {
      setErrorModal({
        open: true,
        title: 'Schedule',
        message: SCHEDULE_LOCATION_ERROR,
        showPestPacActions: true,
        onRetry: () => runScheduleFlow(),
      })
      return
    }

    if (freshEntityDetails.servicesCount <= 0) {
      setErrorModal({
        open: true,
        title: 'Schedule',
        message:
          'You must add a service to this record before you can schedule.',
        showAddServiceButton: true,
      })
      return
    }
    setAnchorEl(scheduleButtonRef.current)
  }

  const handleViewClick = () => {
    runScheduleFlow()
  }

  const {
    freeEstimateScheduleEnabled,
    paidEstimateScheduleEnabled,
    serviceScheduleEnabled,
    requireFreeEstimateSchedule,
    requirePaidEstimateSchedule,
    requireServiceSchedule,
    requireACWOppsSchedulingService,
    entityId,
    scheduleSettings,
  } = useMemo(() => {
    const details: any = entityDetails
    return {
      freeEstimateScheduleEnabled:
        details?.scheduleSettings?.scheduleVisibilitySettings
          ?.freeEstimateScheduleEnabled || false,
      paidEstimateScheduleEnabled:
        details?.scheduleSettings?.scheduleVisibilitySettings
          ?.paidEstimateScheduleEnabled || false,
      serviceScheduleEnabled:
        details?.scheduleSettings?.scheduleVisibilitySettings
          ?.serviceScheduleEnabled || false,
      requireFreeEstimateSchedule:
        details?.scheduleSettings?.scheduleClosingRequirements
          ?.requireFreeEstimateSchedule || false,
      requirePaidEstimateSchedule:
        details?.scheduleSettings?.scheduleClosingRequirements
          ?.requirePaidEstimateSchedule || false,
      requireServiceSchedule:
        details?.scheduleSettings?.scheduleClosingRequirements
          ?.requireServiceSchedule || false,
      requireACWOppsSchedulingService:
        details?.scheduleSettings?.scheduleClosingRequirements
          ?.requireACWOppsSchedulingService || false,
      entityId: details?.id,
      scheduleSettings: details?.scheduleSettings,
    }
  }, [entityDetails])

  const schedulingEnabled = useSchedulingEnabled()
  const schedulingVisible = useSchedulingVisibility(scheduleSettings)
  const showScheduling = schedulingEnabled && schedulingVisible

  if (showScheduling && scheduleSettings) {
    return (
      <CWQDialogContextProvider>
        <SchedulingWizardContextProvider
          entityId={entityId}
          entityType={entityType}
          salesFunnelId={Number(entityDetails?.salesFunnelId)}
          onLeadAutoClosed={onLeadAutoClosed}
        >
          <Button
            fullWidth
            size="large"
            variant="contained"
            id={'scheduled-button'}
            ref={scheduleButtonRef}
            sx={{
              backgroundColor: '#D4E3F3',
            }}
            onClick={handleViewClick}
            color="secondary"
          >
            {isLoading ? <CircularProgress size={20} /> : 'Schedule'}
          </Button>
          <Menu
            id="scheduled-menu"
            anchorEl={anchorEl}
            open={open}
            elevation={8}
            onClose={handleClose}
            MenuListProps={{
              'aria-labelledby': 'scheduled-button',
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            sx={{ mt: 0.8 }}
            slotProps={{
              paper: {
                sx: {
                  minWidth:
                    document?.getElementById('scheduled-button')?.offsetWidth ||
                    'auto',
                },
              },
            }}
          >
            {freeEstimateScheduleEnabled && (
              <ScheduleMenuItem
                mode="free"
                onClose={handleClose}
                entityId={entityId}
                required={requireFreeEstimateSchedule}
                disabled={!freeEstimateScheduleEnabled}
              />
            )}
            {paidEstimateScheduleEnabled && (
              <ScheduleMenuItem
                mode="paid"
                onClose={handleClose}
                entityId={entityId}
                required={requirePaidEstimateSchedule}
                disabled={!paidEstimateScheduleEnabled}
              />
            )}

            {serviceScheduleEnabled && (
              <ScheduleMenuItem
                mode="service"
                onClose={handleClose}
                entityId={entityId}
                required={requireServiceSchedule}
                disabled={!serviceScheduleEnabled}
                requireACWOppScheduling={
                  serviceScheduleEnabled &&
                  requireServiceSchedule &&
                  requireACWOppsSchedulingService &&
                  entityType == ENTITY_TYPES.Opportunity
                }
              />
            )}
          </Menu>
        </SchedulingWizardContextProvider>
      </CWQDialogContextProvider>
    )
  }

  return null
}
